<template>
    <div class="d-flex align-center">
        <v-text-field
            v-if="state.mode === 'single'"
            outlined
            dense
            :value="textfieldDisplayValue"
            placeholder="(nach dem Speichern ersichtlich)"
            readonly
        />
        <v-select
            v-else-if="state.mode === 'multi'"
            :value="event?.organizer_id"
            outlined
            dense
            :items="state.eventOrganizerCollection.models"
            item-text="displayTitle"
            item-value="id"
            :placeholder="$t('placeholder_event_info_categories_organizer_id')"
            hide-details="auto"
            clearable
            return-object
            :rules="required ? [formRules.required] : []"
            @change="onSelect"
        >
            <template #item="{ item, on, attrs }">
                <AddressComboListItem :item="item" v-bind="attrs" v-on="on" />
            </template>
        </v-select>
        <AutocompleteDisplay
            v-else-if="state.mode === 'staff'"
            :selected="eoToEdit"
            :display-value="textfieldDisplayValue"
            :collection="state.eventOrganizerCollection"
            item-text="displayTitle"
            item-value="id"
            :placeholder="$t('placeholder_event_info_categories_organizer_id')"
            display-hide-details="auto"
            query-param="simpleQuery"
            clearable
            :rules="required ? [formRules.required] : []"
            @selected="onSelect"
        >
            <template #item="{ item, on, attrs }">
                <AddressComboListItem :item="item" v-bind="attrs" v-on="on" />
            </template>
        </AutocompleteDisplay>
        <div v-else>No supported role</div>
        <IconBtn
            v-if="showEdit && event?.organizer_id"
            :disabled="form?.disabled"
            margin-class="ma-0"
            color="transparent"
            icon-cls="pencil"
            icon-color="black"
            :tooltip="$t('to_eo_edit')"
            :loading="state.eoLoading"
            @click="editEo"
        />
        <IconBtn
            v-if="showNew"
            margin-class="ma-0"
            :disabled="form?.disabled"
            color="transparent"
            icon-cls="new"
            icon-color="black"
            :tooltip="$t('create_organizer')"
            @click="newOrganizer"
        />
        <EditDialog
            v-if="eoToEdit && state.showEditDlg"
            v-model="state.showEditDlg"
            :event-organizer="eoToEdit"
            :writable="state.showDlgWritable"
            @address-changed="eventOrganizerChanged"
            @event-organizer-changed="eventOrganizerChanged"
        />
    </div>
</template>

<script lang="ts" setup>
import { inject, reactive, ref, toRefs, watchEffect } from 'vue';
import { formRules } from '@/helpers/tools';
import { userIsStaff } from '@/helpers/auth';
import { instance as api } from '@/helpers/api';
import { getStore } from '@/store';
import { Event } from '@/models/Event';
import { EventOrganizer, EventOrganizerCollection } from '@/models/EventOrganizer';
import AddressComboListItem from '@/components/address/AddressComboListItem.vue';
import EditDialog from '@/components/event_organizer/EditDialog.vue';
import { Address } from '@/models/Address';

const props = defineProps({
    event: {
        type: Event,
        required: false,
        default: null
    },
    showEdit: {
        type: Boolean,
        default: true,
    },
    showNew: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: true,
    },
});
const { event, showEdit } = toRefs(props);

const eoCollection = new EventOrganizerCollection();
if (getEditMode() === 'multi') {
    eoCollection.query();
}

const state = reactive({
    eventOrganizerCollection: eoCollection,
    mode: getEditMode(),
    eoLoading: false,
    showDlgWritable: false,
    showEditDlg: false,
});

const eoToEdit = ref<EventOrganizer | null>(null);

// This component may be part of a form component: We want to check
// for form props like validity and disabled state:
const form: any = inject('form', null);

const emit = defineEmits(['selected']);
const textfieldDisplayValue = ref('');

watchEffect(async () => {
    // Whenever the organizer changes, we load it, so that we can pre-select
    // the autocomplete store.
    if (props.event?.organizer_id && eoToEdit.value?.id !== props.event?.organizer_id) {
        const eo = await loadEoForId(props.event?.organizer_id);
        eoToEdit.value = eo as EventOrganizer | null;
        // we add the eo to the autocomplete store, so that we can display it
        // in the text field when clicked into:
        if (eo) {
            if (!state.eventOrganizerCollection.find((entry: EventOrganizer) => entry.id === eo.id)) {
                state.eventOrganizerCollection.add(eo);
            }
        }
    }

    // We need to set the textfield's display value, depending on the type of event.event_organizer's value:
    if (typeof props.event?.event_organizer === 'string') {
        textfieldDisplayValue.value = props.event?.event_organizer;
    } else if (props.event?.event_organizer?.title) {
        textfieldDisplayValue.value = props.event?.event_organizer?.title;
    } else {
        textfieldDisplayValue.value = '';
    }
});

/**
 * The field knows 3 different entry modes:
 * - single: if the actual user is an EventOrganizer without OrganizerGroup, it can only show its own EO, no edit.
 * - multi: if the actual user is an EventOrganizer WITH OrganizerGroup, it can select from its assigned EOs
 * - staff: Staff members can see all EOs
 */
function getEditMode(): 'single' | 'multi' | 'staff' {
    const store = getStore();
    if (userIsStaff()) {
        return 'staff';
    }
    if (store.state.user && store.state.user.record.organizer_group_id > 0) {
        return 'multi';
    }
    return 'single';
}

async function editEo() {
    const eo = eoToEdit.value;
    if (eo) {
        const isWritable = await loadIsWritable(eo);
        // eoToEdit.value = eo;
        state.showDlgWritable = isWritable;
        state.showEditDlg = true;
    }
}

async function loadIsWritable(eo: EventOrganizer) {
    const res = await api().get('/EventOrganizer/isWritable', {
        params: {
            id: eo.id,
        },
    });
    return res?.data?.writable === true;
}

async function eventOrganizerChanged(data: { address: Address; eventOrganizer: EventOrganizer }) {
    const eo = await loadEoForId(data.eventOrganizer?.getId());
    if (eo) {
        emit('selected', eo);
    }
    return eo;
}

function onSelect(ev: any) {
    // Note: We need a setTimeout here:
    // when the selected event is triggered,
    // the field's validation state has not yet
    // changed: That means, the new value will be propagated,
    // but the field (and so the surrounding form) is still
    // marked as invalid, until in one of the next n run loops.
    // So we give things some time to settle here.
    // I _guess_ this is a vuetify bug/inconsistency.
    window.setTimeout(() => {
        emit('selected', ev);
    }, 50);
}

async function loadEoForId(id: number) {
    if (!id) {
        return null;
    }
    const collection = new EventOrganizerCollection();
    await collection.query({ id });
    return collection.first();
}

function newOrganizer() {
    eoToEdit.value = new EventOrganizer();
    newEo();
}

function newEo() {
    const eo = eoToEdit.value;
    if (eo) {
        const isWritable = true;
        state.showDlgWritable = isWritable;
        state.showEditDlg = true;
    }
}
</script>
